import { PageObj, InputObj, PageItem, InputValues, compareInputObj } from '../Interfaces/TestCaseInterface';
import { InputEditor } from './InputEditor';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import cloneDeep from 'lodash/cloneDeep'
import './PageEditor.css'
import { InfoTooltip } from './InfoTooltip';
import { nanoid } from 'nanoid';

interface PageEditorProps {
  page: PageObj,
  setPage: (iid: number, newPage: PageObj) => void,
  pagePolicyInputs?: Array<string>,
  policyVariables: Record<string, PageItem>,
  inputError: boolean,
  editable: boolean
}

export const PageEditor = (props: PageEditorProps) => {

  const updateInputIx = (iid: string, adjustment: number) => {
    
    let oldIx: number = props.page.inputs.findIndex((e: InputObj) => e.iid === iid);
    let newInputs = [...props.page.inputs];
    if (adjustment + oldIx < 0)
      adjustment = oldIx;
    if (adjustment + oldIx > props.page.inputs.length - 1)
      adjustment = props.page.inputs.length - 1 - oldIx;
    if (adjustment === 0)
      return;
    let oldPage = props.page.inputs[oldIx];
    if (adjustment < 0) {
      for (let i = 0; i > adjustment; i--) {
        newInputs[oldIx + i] = newInputs[oldIx + i - 1];
      }
      newInputs[oldIx + adjustment] = oldPage;
    } else { // adjustment > 0
      for (let i = 0; i < adjustment; i++) {
        newInputs[oldIx + i] = newInputs[oldIx + i + 1];
      }
      newInputs[oldIx + adjustment] = oldPage;
    }
    
    if (JSON.stringify(newInputs) !== JSON.stringify(props.page.inputs)) {
      props.setPage(props.page.pid, {...props.page, inputs: newInputs});
    }
  } // updateInputIx

  const deleteInput = (iid: string) => {
    let newInputs = props.page.inputs.filter(e => e.iid !== iid);
    //newInputs.forEach((input, index) => { newInputs[index] = { ...input, iid: nanoid() } });
    props.setPage(props.page.pid, {...props.page, inputs: newInputs});
  } // deleteInput()

  const setInput = (iid: string, newInput: InputObj) => {
    
    let replaceIx: number = props.page.inputs.findIndex((e: InputObj) => e.iid === iid);
    if(compareInputObj(newInput, props.page.inputs[replaceIx])){
      return;
    }
    let newPageInputs = cloneDeep(props.page.inputs); // clone page from props
    newPageInputs.splice(replaceIx, 1, newInput);
    if (JSON.stringify(newPageInputs[replaceIx]) !== JSON.stringify(props.page.inputs[replaceIx])) {
      props.setPage(props.page.pid, {...props.page, inputs: newPageInputs});
    }
    
  } // setInput()

  const addInput = () => {
    let policyUploaded = props.pagePolicyInputs !== undefined && props.pagePolicyInputs.length > 0;
    let newInput: InputObj = {
      iid: nanoid(),
      policyInput: policyUploaded ? "policy" : "custom",
      id: policyUploaded ? props.policyVariables[props.pagePolicyInputs![0]].id : "",
      inputType: policyUploaded ? props.policyVariables[props.pagePolicyInputs![0]].input : InputValues.Text,
      value: ""
    }
    props.setPage(props.page.pid, {...props.page, inputs: props.page.inputs.concat(newInput)});
  } // addPage()

  return (
    <Box className="pageEditor">
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant='h5'>
            Actions on {props.page.name}
          </Typography>
        </Box>
        <Box sx={{ alignContent: 'end' }}>
          <Button variant='outlined' color='secondary' size='small' disabled={!props.editable || (props.page.inputs.length > 0 && props.page.inputs[props.page.inputs.length - 1].inputType === InputValues.Navigate)} onClick={e => addInput()}>Add Input</Button>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ overflowX: 'auto' }} size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '100px' }} className='same-line'>
                Input Source
                <InfoTooltip
                  position='bottom'
                  helpText={
                    [
                      'Policy: Elements placed on the page by the B2C policy that has been uploaded.',
                      'Function: Predefined functions in the test runner.',
                      'Custom: HTML Element that is not part of policy.'
                    ]} />
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }} className='same-line'>
                Id
                <InfoTooltip position='bottom' helpText='Id: Id of the HTML element.' />
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }} className='same-line'>
                Input type
                <InfoTooltip position='bottom' helpText='Input type: Type of the HTML element, use Button for links.' />
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }} className='same-line'>
                Value
                <InfoTooltip position='bottom' helpText='Value: User specific values used for testing, can be blank for buttons.' />
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody className='test-case-column'>
            {props.page.inputs.map((input: InputObj, index: number) => {
              return <InputEditor
                key={input.iid}
                input={input}
                setInput={setInput}
                updateInputIx={updateInputIx}
                deleteInput={deleteInput}
                policyVariables={props.policyVariables}
                pagePolicyInputs={props.pagePolicyInputs}
                lastItem={index === props.page.inputs.length - 1}
                inputError={props.inputError}
                editable={props.editable}
              />
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}